import { Avatar, Box, Typography } from '@mui/material';

export interface IEmployeeAvatar {
  id: number;
  name: string;
  profilePicture?: string;
  color?: string;
}

interface IEmployeeAvatarSelectorProps {
  employees: IEmployeeAvatar[];
  value?: number;
  onChange: (id?: number) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  label?: string;
  renderExtra?: (emp: IEmployeeAvatar) => React.ReactNode;
  scrollable?: boolean;
  justifyContent?: string;
  compact?: boolean;
}

export const EmployeeAvatarSelector: React.FC<IEmployeeAvatarSelectorProps> = ({
  employees,
  value,
  onChange,
  disabled = false,
  size = 'medium',
  label,
  renderExtra,
  scrollable = false,
  justifyContent = 'flex-start',
  compact = false,
}) => {
  const avatarSize = size === 'small' ? 40 : 48;

  return (
    <Box>
      {label && (
        <Typography variant='subtitle2' sx={{ mb: scrollable ? 0.5 : 1, color: 'text.secondary' }}>
          {label}
        </Typography>
      )}
      <Box
        display='flex'
        gap={compact ? 1 : 1.5}
        px={0.5}
        sx={{
          flexWrap: scrollable ? 'nowrap' : 'wrap',
          justifyContent,
          overflowX: scrollable ? 'auto' : 'visible',
          overflowY: 'hidden',
          py: 0,
          scrollbarWidth: 'thin',
          scrollbarColor: scrollable ? 'rgba(0,0,0,0.1) transparent' : 'transparent transparent',
          transition: 'scrollbar-color 0.2s',
          '&:hover': scrollable ? { scrollbarColor: 'rgba(0,0,0,0.25) transparent' } : {},
          '&::-webkit-scrollbar': { height: 2, width: 0, background: 'transparent' },
          '&::-webkit-scrollbar-track': { background: 'transparent', border: 'none', boxShadow: 'none' },
          '&::-webkit-scrollbar-thumb': { background: scrollable ? 'rgba(0,0,0,0.1)' : 'transparent', borderRadius: 1, border: 'none' },
          '&:hover::-webkit-scrollbar-thumb': scrollable ? { background: 'rgba(0,0,0,0.25)' } : {},
        }}
      >
        {employees.map((emp) => {
          const isSelected = value === emp.id;
          const cardWidth = compact ? 56 : size === 'small' ? 72 : 88;
          return (
            <Box
              key={emp.id}
              onClick={() => !disabled && onChange(isSelected ? undefined : emp.id)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0,
                width: cardWidth,
                cursor: disabled ? 'default' : 'pointer',
                opacity: isSelected ? 1 : 0.7,
                transition: 'opacity 0.2s, transform 0.2s, border-color 0.2s, background-color 0.2s',
                '&:hover': !disabled ? { opacity: 1, transform: 'scale(1.05)' } : {},
                border: '1px solid',
                borderColor: isSelected ? 'primary.main' : 'transparent',
                borderRadius: 2,
                p: compact ? 0.5 : 1.5,
                bgcolor: isSelected ? 'primary.softBg' : 'transparent',
              }}
            >
              <Avatar
                src={emp.profilePicture}
                slotProps={{ img: { loading: 'lazy' } }}
                sx={{
                  width: avatarSize,
                  height: avatarSize,
                  border: isSelected ? '3px solid' : '2px solid transparent',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: emp.color || 'action.selected',
                  transition: 'border-color 0.2s',
                }}
              >
                {emp.name?.charAt(0).toUpperCase()}
              </Avatar>
              {!compact && (
                <Typography
                  variant='caption'
                  align='center'
                  sx={{
                    fontWeight: 400,
                    color: isSelected ? 'primary.main' : 'text.secondary',
                    maxWidth: '100%',
                    lineHeight: 1.2,
                    fontSize: '0.65rem',
                    mb: 0.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {emp.name.length > 20 ? emp.name.split(' ')[0] : emp.name}
                </Typography>
              )}
              {renderExtra?.(emp)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
